import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import HelpIcon from '@mui/icons-material/Help';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";

import PrimaryLayout from "../components/layout/PrimaryLayout";
import DataTable from "../components/tables/DataTable";
import { Column } from "../components/tables/interfaces";
import { executionsApiClient } from "../services/api-client";
import { V1ListWorkflowExecutionsResponse, V1WorkflowExecutionStatus } from "../services/workflow-server";
import { Refresh, Search } from "@mui/icons-material";

const Executions = () => {
  const navigate = useNavigate();
  const { namespace } = useParams();
  console.log("route namespace", namespace)
  const [selectedNamespace, setSelectedNamespace] = useState(namespace || "default");
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [selectedField, setSelectedField] = useState("ExecutionStatus");
  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const searchFields = ["ExecutionStatus", "WorkflowId", "WorkflowType", "ExecutionTime", "HistoryLength", "RunId",
    "TaskQueue", "StartTime", "CloseTime"];

  console.log("Selected Namespace: ", selectedNamespace);
  const columns: Column[] = [
    {
      id: 'status', label: 'Status', render: (value: any) => {
        let chipColor: 'primary' | 'secondary' | 'warning' | 'success' | 'error' | 'info' = 'info';
        console.log(value);
        console.log(V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_COMPLETED);
        switch (value.toLowerCase()) {
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_COMPLETED.toLowerCase():
            chipColor = 'success';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_FAILED.toLowerCase():
            chipColor = 'error';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_RUNNING.toLowerCase():
            chipColor = 'info';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_CANCELED.toLowerCase():
            chipColor = 'warning';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_TERMINATED.toLowerCase():
            chipColor = 'error';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_TIMED_OUT.toLowerCase():
            chipColor = 'primary';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_CONTINUED_AS_NEW.toLowerCase():
            chipColor = 'info';
            break;
          case V1WorkflowExecutionStatus.WORKFLOW_EXECUTION_STATUS_UNSPECIFIED.toLowerCase():
            chipColor = 'info';
            break;
        }
        return <Chip label={value} size="small" color={chipColor} />
      },
    },
    {
      id: "workflow_id",
      label: "Workflow ID",
      type: 'string',
      onCellClick: (value, row) => navigate(`/executions/${selectedNamespace}/${row.workflow_id}/${row.id}?namespace=${selectedNamespace}`)
    },
    { id: "type", label: "Type", type: 'string' },
    { id: "start_time", label: "Start Time", type: "datetime" },
    { id: "end_time", label: "End Time", type: "datetime" },
  ];

  const fetchData = useCallback(async (query?: { pageSize?: number; nextPageToken?: string; query?: string; } | { page?: number; limit?: number }) => {
    console.log(query);
    if (query && ('pageSize' in query || 'nextPageToken' in query)) {
      try {
        const response = await executionsApiClient.api.workflowServiceListWorkflowExecutions(selectedNamespace, query);
        console.log(response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching workflow executions:', error);
        return {
          executions: [],
          nextPageToken: undefined,
        } as V1ListWorkflowExecutionsResponse;
      }
    }
    else {
      return {
        executions: [],
        nextPageToken: undefined,
      } as V1ListWorkflowExecutionsResponse;
    }
  }, [selectedNamespace])

  const dataExtractor = useCallback((response: V1ListWorkflowExecutionsResponse): any[] => {
    return (response.executions || []).map(execution => ({
      id: execution.execution?.runId,
      status: execution.status,
      workflow_id: execution.execution?.workflowId,
      type: execution.type?.name,
      start_time: execution.startTime,
      end_time: execution.closeTime
    }));
  }, [])

  const nextPageTokenExtractor = useCallback((response: V1ListWorkflowExecutionsResponse): string | undefined => response.nextPageToken, [])

  const handleNamespaceChange = (event: any, value: string | null) => {
    if (value) {
      setSelectedNamespace(value);
      navigate(`/executions/${value}`);
    }
  };

  const handleSearch = () => {
    if (selectedField && searchValue) {
      const query = `${selectedField}='${searchValue}'`;
      setSearchQuery(query);
      console.log(query);
    } else {
      setSearchQuery('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    const fetchInitialNamespaces = async () => {
      try {
        const response = await executionsApiClient.api.workflowServiceListNamespaces();
        const namespace = (response.data.namespaces || []).map(namespace => (namespace.namespaceInfo?.name)).filter((name): name is string => !!name);
        console.log("Namespace", namespace);
        setNamespaces(namespace);
      } catch (error) {
        console.error('Error fetching namespaces:', error);
      }
    };

    fetchInitialNamespaces();
  }, []);

  useEffect(() => {
    if (!namespace) {
      navigate(`/executions/${selectedNamespace}`, { replace: true });
    }
  }, [namespace, selectedNamespace, navigate]);

  useEffect(() => {
    const getData = async () => {
      const result = await fetchData();
    };
    getData();
  }, [refreshKey]);

  return (
    <PrimaryLayout>
      <Box sx={{ mx: 2, my: 1 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Executions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor every workflow run in real time — see status, timing, and key metadata as executions finish.
          Select any run to dive into its full trace: inputs, outputs, and each activity step for instant troubleshooting and audit.
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" sx={{ m: 2 }}>
        <Autocomplete
          options={namespaces}
          value={selectedNamespace}
          autoHighlight
          onChange={handleNamespaceChange}
          sx={{ minWidth: 300, height: 56, mr: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Namespace"
              variant="outlined"
              size="small"
            />
          )}
        />
        <FormControl sx={{ mr: 2, minWidth: 160 }}>
          <InputLabel id="select-field-label">Search Parameters</InputLabel>
          <Select
            labelId="select-field-label"
            value={selectedField}
            label="Search Parameters"
            onChange={(e) => setSelectedField(e.target.value)}
          >
            {searchFields.map((field) => (
              <MenuItem key={field} value={field}>
                {field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          sx={{ mr: 2, minWidth: 300 }}
          label="Search Value"
          variant="outlined"
          value={searchValue}
          onChange={async (e) => {
            const newValue = e.target.value;
            setSearchValue(newValue);
            if (newValue === "") {
              await new Promise(() => setSearchQuery(""));
            } else {
              await new Promise(() => setSearchQuery(searchQuery));
            }
          }}
          onKeyUp={handleKeyPress}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="For ExecutionStatus search with status like Completed or Failed or Running" sx={{ cursor: 'pointer' }}>
                  <HelpIcon />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        {/* <IconButton color="primary" onClick={handleSearch}>
          <SearchIcon />
        </IconButton> */}

        <Box display="flex" sx={{ m: 2}} gap={2} >
          <Button
            variant="contained"
            color='primary'
            startIcon={<Search />}
            onClick={handleSearch}
          >
            Search
          </Button>


          <Button
            variant="contained"
            color='secondary'
            startIcon={<Refresh />}
            onClick={() => setRefreshKey(refreshKey + 1)}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      <Box key={refreshKey}>
        <DataTable
          columns={columns}
          fetchData={searchQuery === '' ? fetchData : (pageQuery) => fetchData({ ...pageQuery, query: searchQuery })}
          dataExtractor={dataExtractor}
          nextPageTokenExtractor={nextPageTokenExtractor}
        />
      </Box>
    </PrimaryLayout>
  );
};

export default Executions;
